import { useCallback } from "react";

import type { ClockLocationInput } from "@/adapters/api";

export const GEOLOCATION_OPTIONS: PositionOptions = {
	enableHighAccuracy: true,
	timeout: 10_000,
	maximumAge: 60_000,
};

export interface GeolocationSource {
	isSecureContext: boolean;
	geolocation: Pick<Geolocation, "getCurrentPosition"> | undefined;
}

function toClockLocation(position: GeolocationPosition): ClockLocationInput {
	const { latitude, longitude, accuracy } = position.coords;
	return {
		lat: latitude,
		lng: longitude,
		accuracyM: Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : null,
	};
}

export function requestLocation(
	source: GeolocationSource,
	deadlineMs: number = GEOLOCATION_OPTIONS.timeout ?? 0,
): Promise<ClockLocationInput | null> {
	if (!source.isSecureContext || !source.geolocation) {
		return Promise.resolve(null);
	}
	const geolocation = source.geolocation;
	return new Promise((resolve) => {
		const deadline = setTimeout(() => resolve(null), deadlineMs);
		const settle = (location: ClockLocationInput | null) => {
			clearTimeout(deadline);
			resolve(location);
		};
		try {
			geolocation.getCurrentPosition(
				(position) => settle(toClockLocation(position)),
				() => settle(null),
				GEOLOCATION_OPTIONS,
			);
		} catch {
			settle(null);
		}
	});
}

function browserSource(): GeolocationSource {
	if (typeof window === "undefined" || typeof navigator === "undefined") {
		return { isSecureContext: false, geolocation: undefined };
	}
	return {
		isSecureContext: window.isSecureContext,
		geolocation: "geolocation" in navigator ? navigator.geolocation : undefined,
	};
}

export function useGeolocation() {
	return useCallback(() => requestLocation(browserSource()), []);
}
